import classNames from "classnames";
import React from "react";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { push } from "react-router-redux";
import { Dispatch } from "redux";

import { buildStructuredDataSnippet } from "../../shared/helpers/metaData";
import { BannerPrimary, Footer } from "@travellocal/ui";
import { Header } from "../components/layout/header";
import { isBrowser } from "./../../core/detection";
import { ActionPromises } from "./../state/actionPromises";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import { SiteRoot } from '../../shared/components/siteRoot';
import { HubspotNewsletterSignupForm } from "../components/hubspotForm/hubspotForm";

import "./shell.scss";

// TODO: This will eventually be supplied by a server-side API
const ReviewsRating = {
  ratingValue: 4.91,
  reviewCount: 353,
  bestRating: 5,
  worstRating: 1,
};

export interface IShellComponentProps {
  className?: string;
  children?: JSX.Element;
}

interface IShellRouteParams extends RouteComponentProps<{}, { enquiryId: string }> {
}

const dispatchToProps = (dispatch: Dispatch<IWebStoreState>) => ({
  navigateToPath: (path: string) => dispatch(push(path)),
  getEnquiry: (enquiryId: number) => dispatch(ActionPromises.enquiryById(enquiryId)),
});

const ShellHelmet = () => {
  const structuredData = {
    name: "TravelLocal",
    logo: `${globalConfig.wwwBaseUrl}/images/logo-green.svg`,
    image: `${globalConfig.wwwBaseUrl}/images/logo-green.svg`,
    url: globalConfig.wwwBaseUrl,
    telephone: "+44 (0)117 325 7898",
    paymentAccepted: "Visa and Mastercard",
    isicV4: "N791",
    email: "team@travellocal.com",
    address: {
      "@type": "PostalAddress",
      "addressLocality": "Bristol, United Kingdom",
      "streetAddress": "Runway East, 1 Victoria St, Redcliffe",
      "postalCode": "BS1 6AA",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      "ratingValue": ReviewsRating.ratingValue,
      "ratingCount": ReviewsRating.reviewCount,
      "reviewCount": ReviewsRating.reviewCount,
      "bestRating": ReviewsRating.bestRating,
      "worstRating": ReviewsRating.worstRating,
    },
  };

  const structuredDataSnippet = buildStructuredDataSnippet("TravelAgency", structuredData);
  return (
    <Helmet
      titleTemplate="%s | TravelLocal"
      defaultTitle="TravelLocal"
      link={[
        { rel: "apple-touch-icon", sizes: "180x180", href: "/favicons/apple-touch-icon.png" },
        { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicons/favicon-32x32.png" },
        { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicons/favicon-16x16.png" },
        { rel: "manifest", href: "/favicons/site.webmanifest" },
        { rel: "shortcut icon", href: "/favicons/favicon.ico" },
      ]}
      meta={[
        { name: "application-name", content: "TravelLocal" },
        { name: "msapplication-TileColor", content: "#da532c" },
        { name: "msapplication-TileImage", content: "/favicons/mstile-150x150.png" },
      ]}
      script={[structuredDataSnippet]}
    />
  );
}

type Props = IShellComponentProps & IShellRouteParams & ReturnType<typeof dispatchToProps>;

const ShellComponent = (props: Props) => {

  // Redirect to payment overview page when iframe sends completion message.
  // TODO is this the best place for this? This relies on enquiryId being in react-router
  // context, but this is only the case in /customer routes
  useEffect(() => {
    if (!isBrowser()) {
      return;
    }

    const onPaymentIframeMessage = event => {
      if (event.origin !== globalConfig.paymentBaseUrl) {
        return;
      }
      if (event.data === "payment-success" || event.data === "payment-failure") {
        const enquiryId = parseInt(props.params.enquiryId);
        if (enquiryId != null && !isNaN(enquiryId)) {
          props.navigateToPath(`${WebRoutes.customerEnquiryPayment(enquiryId)}?result=${event.data}`);
          props.getEnquiry(enquiryId);
        }
      }
    };

    window.addEventListener("message", onPaymentIframeMessage);
    return () => window.removeEventListener("message", onPaymentIframeMessage);
  }, []);

  return (
    <SiteRoot className={classNames("marketing", props.className)}>
      <ShellHelmet />
      <Header />
      <BannerPrimary
        heading={"Our Peace of Mind Guarantee"}
        description={"Flexible booking  |  Financially protected  |  Trusted by Which?"}
        label={"Find out more"}
        to={"https://go.travellocal.com/book-with-confidence"}
      />
      <main className="main" id="main">
        {props.children}
      </main>
      <Footer renderForm={() => <HubspotNewsletterSignupForm />} />
    </SiteRoot>
  );
};

export const Shell = connect(null, dispatchToProps)(ShellComponent);
